/**
 * Shared Lingui helper for ``@ando/ui`` — tolerates a missing
 * ``<I18nProvider>`` by falling back to the module-level ``@lingui/core``
 * singleton.
 *
 * Why not ``useLingui`` directly?
 *
 *   ``useLingui()`` from ``@lingui/react`` (v5) throws in dev/test when
 *   invoked outside an ``<I18nProvider>``. ``packages/ui`` is rendered in
 *   3 environments where the provider may be absent:
 *
 *     1. Vitest unit tests — minimal jsdom setup, no app shell
 *     2. Storybook (future) — preview iframes without the app provider
 *     3. SSR snapshots — the consumer may bootstrap the provider after
 *        the first render
 *
 *   This hook short-circuits the throw: it reads ``I18nContext`` directly,
 *   and when ``null`` returns the activated module-level ``i18n`` so
 *   ``i18n._(msg`Foo`)`` still resolves to the source string. Apps that
 *   wrap the tree in ``<I18nProvider>`` keep their reactive locale-switch
 *   semantics unchanged.
 *
 *   This is the SINGLE point of i18n access for shared primitives —
 *   never import ``useLingui`` from ``@lingui/react`` directly inside
 *   ``packages/ui`` code.
 */

import * as React from "react";
import { i18n as fallbackI18n, type I18n } from "@lingui/core";
import { LinguiContext } from "@lingui/react";

interface I18nContextValue {
  readonly i18n: I18n;
}

/**
 * Returns the active Lingui ``i18n`` instance.
 *
 * Behaves like ``useLingui()`` when an ``<I18nProvider>`` wraps the tree;
 * otherwise falls back to the module-level singleton so primitives stay
 * renderable in tests / Storybook / pre-bootstrap SSR.
 */
export function useI18n(): I18nContextValue {
  const ctx = React.useContext(
    LinguiContext as unknown as React.Context<I18nContextValue | null>,
  );
  if (ctx) return ctx;
  return { i18n: fallbackI18n };
}
